<script>
import Multiselect from 'vue-multiselect'
import PhoneNumberInput from '@/system/components/global/form/PhoneNumberInput';
import { ErrorBag } from '@/system/helpers/Validate';
import CountrySelect from '@/system/components/global/form/CountrySelect.vue';
import DatePicker from '@/system/components/global/form/DatePicker.vue';

const DEFAULT_OPTIONS = { display: true, required: false }

const validator = (value) => {
    return ['display', 'required'].every((property) => Object.keys(value).some((prop) => prop === property))
}

export default {
    components: {
        Multiselect,
        PhoneNumberInput,
        CountrySelect,
        DatePicker,
    },
    props: {
        value: {
            type: Object,
            required: true
        },
        nameOptions: {
            type: Object,
            required: false,
            default: () => {
                return DEFAULT_OPTIONS
            },
            validator
        },
        emailOptions: {
            type: Object,
            required: false,
            default: () => {
                return DEFAULT_OPTIONS
            },
            validator
        },
        phoneOptions: {
            type: Object,
            required: false,
            default: () => {
                return {
                    ...DEFAULT_OPTIONS,
                    dropdownParent: '#fill-info-frm',
                }
            },
            validator
        },
        addressOptions: {
            type: Object,
            required: false,
            default: () => {
                return DEFAULT_OPTIONS
            },
            validator
        },
        cprOptions: {
            type: Object,
            required: false,
            default: () => {
                return DEFAULT_OPTIONS
            }
        },
        dobOptions: {
            type: Object,
            required: false,
            default: () => {
                return DEFAULT_OPTIONS
            }
        },
        validationErrors: {
            type: Object,
            required: false,
            default: () => new ErrorBag()
        },
        horizontal: {
            type: Boolean,
            required: false,
            default: false
        },
        showRequiredIndicator: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data () {
        return {
            showAddressLine2: false
        }
    },
    methods: {
        update (key, value) {
            this.$emit('input', { ...this.value, [key]: value })
        }
    },
    mounted() {
        $.fn.modal.Constructor.prototype.enforceFocus = function() {};
    },
    computed: {
        addressLineLabel () {
            return this.showAddressLine2
                ? trans('clients.address_line')
                : trans('clients.address')
        },
        countrySelectProps () {
            const props = {}
            if (this.addressOptions && this.addressOptions.countrySelectParent) {
                props.id = this.addressOptions.countrySelectParent
            }
            return props
        },
        showStateField () {
            return this.value.country
                ? Config.get(`countries.${this.value.country}.address_state_visible`)
                : true
        },
        showLine2Field () {
            return this.value.country
                ? Config.get(`countries.${this.value.country}.address_line2_visible`)
                : true
        }
    },
    watch: {
        showLine2Field (value) {
            if (!value) {
                this.showAddressLine2 = false
            }
        },
        showAddressLine2 (value) {
            if (value) {
                this.$nextTick(() => this.$refs.addressLine2.focus())
            } else {
                this.update('address_line_2', '')
            }
        },
        showStateField (value) {
            if (!value) {
                this.update('zone', '')
            }
        },
        validationErrors: {
            handler (errors) {
                this.$setErrorBag(errors)
            },
            immediate: true
        }
    }
}
</script>

<template>
    <div>
        <!-- Name Input -->
        <div class="form-group" :class="{ 'has-error' : errors.has('name') }" v-if="nameOptions.display">
            <label class="control-label" :class="{'col-sm-2': horizontal}">
                {{ nameOptions.label ? nameOptions.label : trans('online_booking.fill_name') }}
                <span v-if="showRequiredIndicator && nameOptions.required" class="text-danger">*</span>
            </label>
            <div :class="{ 'col-sm-10': horizontal }">
                <input :value="value.name" @input="update('name', $event.target.value)" id="name" class="form-control" type="text" name="name" autocomplete="name" :required="nameOptions.required">
                <span v-if="errors.has('name')" class="help-block m-b-0">
                    {{ errors.first('name') }}
                </span>
                <small v-if="nameOptions.description" class="text-muted">{{ nameOptions.description }}</small>
            </div>
        </div>

        <!-- Email Input -->
        <div class="form-group" :class="{ 'has-error' : errors.has('email') }"  v-if="emailOptions.display">
            <label class="control-label" :class="{'col-sm-2': horizontal}">
                {{ emailOptions.label ? emailOptions.label : trans('online_booking.fill_email') }}
                <span class="text-danger" v-if="showRequiredIndicator && emailOptions.required">*</span>
            </label>
            <div :class="{ 'col-sm-10': horizontal }">
                <input :value="value.email" @input="update('email', $event.target.value)" id="email" class="form-control" type="text" name="email" autocomplete="email" :required="emailOptions.required">
                <span v-if="errors.has('email')" class="help-block m-b-0">{{ errors.first('email') }}</span>
                <small v-if="emailOptions.description" class="text-muted">{{ emailOptions.description }}</small>
            </div>
        </div>

        <!-- Phone Input -->
        <div class="form-group" :class="{ 'has-error' : errors.has('phone') }" v-if="phoneOptions.display">
            <label class="control-label" :class="{'col-sm-2': horizontal}">
                {{ phoneOptions.label ? phoneOptions.label : trans('online_booking.fill_phone') }}
                <span class="text-danger" v-if="showRequiredIndicator && phoneOptions.required">*</span>
            </label>
            <div :class="{ 'col-sm-10': horizontal }">
                <phone-number-input :value="value.phone" @input="(value) => update('phone', value)" id="telephone-input" name="phone" :required="phoneOptions.required" :dropdownParent="phoneOptions.dropdownParent ? phoneOptions.dropdownParent : null" />
                <span v-if="errors.has('phone')" class="help-block m-b-0">{{ errors.first('phone') }}</span>
                <small v-if="phoneOptions.description" class="text-muted">{{ phoneOptions.description }}</small>
            </div>
        </div>

        <div v-if="addressOptions.display">

            <!-- Line1 Input -->
            <div class="form-group" :class="{ 'has-error' : errors.has('address') }">
                <label class="control-label" :class="{'col-sm-2': horizontal}">
                    {{ addressLineLabel }}
                    <span class="text-danger" v-if="showRequiredIndicator && addressOptions.required">*</span>
                </label>
                <div :class="{ 'col-sm-10': horizontal }">
                    <input :value="value.address" @input="update('address', $event.target.value)" id="address" class="form-control" type="text" name="address" autocomplete="shipping address-line-1" :required="addressOptions.required">
                    <span v-if="errors.has('address')" class="help-block m-b-0">{{ errors.first('address') }}</span>
                    <small v-if="!errors.has('address') && showLine2Field" class="text-muted" style="display: flex;" :style="{'justify-content': showAddressLine2 ? 'space-between': 'flex-end'}">
                        <span v-if="showAddressLine2">{{ trans('clients.address_line_description') }}</span>
                        <a href="#" @click.prevent="showAddressLine2 = !showAddressLine2">
                            {{ showAddressLine2 ? trans('clients.hide_line2') : trans('clients.add_line2') }}
                        </a>
                    </small>
                </div>
            </div>

            <!-- Line 2 Input -->
            <div v-if="showAddressLine2" class="form-group" :class="{ 'has-error' : errors.has('address_line_2') }">
                <label class="control-label" :class="{'col-sm-2': horizontal}">
                    {{ trans('clients.address_line_2') }}
                </label>
                <div :class="{ 'col-sm-10': horizontal }">
                    <input ref="addressLine2" :value="value.address_line_2" @input="update('address_line_2', $event.target.value)" id="address_line_2" class="form-control" type="text" name="address_line_2" autocomplete="shipping address-line-2">
                    <span v-if="errors.has('address_line_2')" class="help-block m-b-0">{{ errors.first('address_line_2') }}</span>
                    <small v-if="!errors.has('address_line_2')" class="text-muted">{{ trans('clients.address_line_2_description') }}</small>
                </div>
            </div>

            <div class="row" v-if="!horizontal">
                <div class="col-12 col-sm-6">
                    <!-- Zip Input -->
                    <div class="form-group" :class="{ 'has-error' : errors.has('zip') }">
                        <label class="control-label">
                            {{ trans('clients.zip') }}
                            <span class="text-danger" v-if="showRequiredIndicator && addressOptions.required">*</span>
                        </label>
                        <input :value="value.zip" @input="update('zip', $event.target.value)" id="zip" type="text" class="form-control" name="zip" autocomplete="shipping postal-code" :required="addressOptions.required">
                        <span v-if="errors.has('zip')" class="help-block m-b-0">{{ errors.first('zip') }}</span>
                    </div>
                </div>
                <div class="col-12 col-sm-6">
                    <!-- City Input -->
                    <div class="form-group" :class="{ 'has-error' : errors.has('city') }">
                        <label class="control-label">
                            {{ trans('clients.city') }}
                            <span class="text-danger" v-if="showRequiredIndicator && addressOptions.required">*</span>
                        </label>
                        <input :value="value.city" @input="update('city', $event.target.value)" id="city" type="text" class="form-control" name="city" autocomplete="shipping address-level2" :required="addressOptions.required">
                        <span v-if="errors.has('city')" class="help-block m-b-0">{{ errors.first('city') }}</span>
                    </div>

                </div>
            </div>
            <div v-else class="row form-group grouped-input">
                <label class="col-sm-2 control-label" for="zip">
                    {{ trans('clients.zip_city') }}
                    <span class="text-danger" v-if="showRequiredIndicator && addressOptions.required">*</span>
                </label>
                <div class="col-sm-10">
                    <div class="row">
                        <div class="col-xs-6" :class="{ 'has-error': errors.has('zip') }">
                            <input
                                :value="value.zip"
                                @input="update('zip', $event.target.value)"
                                type="text"
                                class="form-control"
                                id="zip"
                                name="zip"
                                autocomplete="shipping postal-code"
                                required
                            />
                            <span v-if="errors.has('zip')" class="help-block m-b-0">{{
                                errors.first('zip')
                            }}</span>
                        </div>
                        <div class="col-xs-6" :class="{ 'has-error': errors.has('city') }">
                            <input
                                :value="value.city"
                                @input="update('city', $event.target.value)"
                                type="text"
                                class="form-control"
                                id="city"
                                name="city"
                                autocomplete="shipping address-level2"
                                required
                            />
                            <span v-if="errors.has('city')" class="help-block m-b-0">{{
                                errors.first('city')
                            }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div :class="{'row': showStateField}" v-if="!horizontal">
                <div class="col-12 col-sm-6" v-if="showStateField">
                    <div>
                        <!-- Zone Input -->
                        <div class="form-group" :class="{ 'has-error' : errors.has('zone') }">
                            <label class="control-label">
                                {{ trans('clients.zone') }}
                            </label>
                            <input :value="value.zone" @input="update('zone', $event.target.value)" id="zone" type="text" class="form-control" autocomplete="shipping address-level3" name="zone">
                            <span v-if="errors.has('zone')" class="help-block m-b-0">{{ errors.first('zone') }}</span>
                        </div>
                    </div>
                </div>
                <div :class="{'col-12 col-sm-6': showStateField }">
                    <div>
                        <!-- Country Input -->
                        <div class="form-group" :class="{ 'has-error' : errors.has('country') }">
                            <label class="control-label">
                                {{ trans('clients.country') }}
                                <span class="text-danger" v-if="showRequiredIndicator && addressOptions.required">*</span>
                            </label>
                            <country-select v-bind="countrySelectProps" :value="value.country" @input="(value) => update('country', value)"></country-select>
                            <span v-if="errors.has('country')" class="help-block m-b-0">{{ errors.first('country') }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="row form-group grouped-input">
                <label class="col-sm-2 control-label" for="zone">
                    {{ showStateField ? trans('clients.zone_country') : trans('clients.country') }}
                    <span class="text-danger" v-if="showRequiredIndicator && addressOptions.required">*</span>
                </label>
                <div class="col-sm-10">
                    <div class="row">
                        <div class="col-xs-6" :class="{ 'has-error': errors.has('zone') }" v-if="showStateField">
                            <input
                                :value="value.zone"
                                @input="update('zone', $event.target.value)"
                                type="text"
                                class="form-control"
                                id="zone"
                                name="zone"
                                autocomplete="shipping address-level3"
                            />
                            <span v-if="errors.has('zone')" class="help-block m-b-0">{{
                                errors.first('zone')
                            }}</span>
                        </div>
                        <div class="col-xs-6" :class="{ 'has-error': errors.has('country') }">
                            <country-select v-bind="countrySelectProps" :value="value.country" @input="(value) => update('country', value)"></country-select>
                            <span v-if="errors.has('country')" class="help-block m-b-0">{{
                                errors.first('country')
                            }}</span>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <!-- CPR Input -->
        <div class="form-group" :class="{ 'has-error' : errors.has('cpr') }"  v-if="cprOptions.display">
            <label class="control-label" :class="{'col-sm-2': horizontal}">
                {{ trans('clients.social_security') }}
                <span class="text-danger" v-if="showRequiredIndicator && cprOptions.required">*</span>
            </label>
            <div :class="{ 'col-sm-10': horizontal }">
                <input :value="value.cpr" @input="update('cpr', $event.target.value)" id="cpr" autocomplete="off" class="form-control " type="text" :required="cprOptions.required">
                <span v-if="errors.has('cpr')" class="help-block m-b-0">{{ errors.first('cpr') }}</span>
            </div>
        </div>

        <!-- DoB Input -->
        <div class="form-group" :class="{ 'has-error' : errors.has('dob') }" v-if="dobOptions.display">
            <label class="control-label" :class="{'col-sm-2': horizontal}">
                {{ trans('client.date_of_birth') }}
                <span class="text-danger" v-if="showRequiredIndicator && (dobOptions.required)">*</span>
            </label>
            <div :class="{ 'col-sm-10': horizontal }">
                <date-picker
                    id="dob"
                    :name="'dob'"
                    @input="(value) => update('dob', value)"
                    :isRequired="dobOptions.required === 'on' || dobOptions.required === true"
                    v-model="value.dob"
                ></date-picker>
                <span v-if="errors.has('dob')" class="help-block m-b-0">{{ errors.first('dob') }}</span>
            </div>
        </div>
    </div>
</template>
